import { Form, PageHeader } from 'antd'
import { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { useParams } from 'react-router-dom'

import Error from '../../components/Error'
import FormBlock from '../../components/forms/FormBlock'
import FormWrap from '../../components/forms/FormWrap'
import Loader from '../../components/Loader'
import {
  FixSkuMediaForm,
  FixSkuSizesForm,
} from '../../forms/FixSkus'
import { getFixSkuById, updateFixSku } from '../../network/FixSku'
import { useAuth } from '../../hooks/useAuth'
import { getCurrency } from '../../network/Stores'
import { getCurrencySymbol } from '../../utils/currencyUtils'
import { createUrlWithFiltersFromCurrentURL } from '../../utils/filterUtils'

const EditFixSku = ({ history }) => {
  const [form] = Form.useForm()
  const { id, storeId } = useParams()
  const { user } = useAuth()

  const [currencySymbol, setCurrencySymbol] = useState('¥')

  const fixSkuDetails = useQuery(
    ['getFixSkuById', { id }],
    getFixSkuById,
    {
      refetchOnWindowFocus: false,
    }
  )

  useEffect(() => {
    if (fixSkuDetails.data) {
      form.resetFields()
      form.setFieldsValue(fixSkuDetails.data)
    }
  }, [fixSkuDetails.data, form])

  useEffect(() => {
    const fetchCurrency = async () => {
      if (!storeId) return
      const currencyValue = await getCurrency(storeId)
      const symbol = getCurrencySymbol(currencyValue.currency)
      setCurrencySymbol(symbol)
    }
    fetchCurrency()
  }, [storeId])

  const FixSkuDescriptionForm = {
    title: 'Description',
    key: [],
    sections: [
      {
        columns: 2,
        fields: [
          {
            label: 'Name',
            placeholder: 'Name',
            isRequired: true,
            key: ['name', 'en'],
            pos: {
              col: 1,
              row: 1,
              size: 1,
            },
          },
          ...(!user.franchiseStore
            ? [
                {
                  label: 'Name (中文)',
                  placeholder: 'Chinese name',
                  isRequired: true,
                  key: ['name', 'zh'],
                  pos: {
                    col: 1,
                    row: 2,
                    size: 1,
                  },
                },
              ]
            : []),
          {
            label: 'SKU',
            placeholder: 'Ref ID',
            isRequired: true,
            key: ['sku'],
            pos: {
              col: 1,
              row: 4,
              size: 1,
            },
          },
          {
            label: `Retail price (${currencySymbol})`,
            isRequired: true,
            key: ['retailPrice'],
            pos: {
              col: 1,
              row: 7,
              size: 1,
            },
          },
          {
            label: 'Delivery time (days)',
            isRequired: true,
            key: ['deliveryTime'],
            pos: {
              col: 1,
              row: 8,
              size: 1,
            },
          },
          {
            label: 'Description',
            type: 'text',
            isRequired: true,
            key: ['description', 'en'],
            pos: {
              col: 1,
              row: 9,
              size: 1,
            },
          },
          ...(!user.franchiseStore
            ? [
                {
                  label: 'Description (中文)',
                  type: 'text',
                  isRequired: true,
                  key: ['description', 'zh'],
                  pos: {
                    col: 1,
                    row: 10,
                    size: 1,
                  },
                },
                {
                  label: 'Gender',
                  type: 'basicRadio',
                  isRequired: false,
                  key: ['gender'],
                  options: [
                    { label: 'Male', key: 'male' },
                    { label: 'Female', key: 'female' },
                    { label: 'Both', key: 'both' },
                  ],
                  pos: {
                    col: 1,
                    row: 11,
                    size: 1,
                  },
                },
                {
                  label: 'Customizable',
                  type: 'basicRadio',
                  isRequired: true,
                  key: ['customizable'],
                  options: [
                    { label: 'Yes', key: 'true' },
                    { label: 'No', key: 'false' },
                  ],
                  pos: {
                    col: 1,
                    row: 12,
                    size: 1,
                  },
                },
                {
                  label: 'Comment',
                  type: 'text',
                  isRequired: false,
                  key: ['comment'],
                  pos: {
                    col: 1,
                    row: 13,
                    size: 1,
                  },
                },
              ]
            : []),
        ],
      },
    ],
  }

  return (
    <>
      <PageHeader
        className="site-page-header"
        title="Edit fix SKU"
        onBack={() => {
          const url = createUrlWithFiltersFromCurrentURL('/fixed-sku')
          history.push(url)
        }}
        style={{ backgroundColor: '#fff' }}
      />
      {fixSkuDetails.isError ? (
        <Error retry={() => fixSkuDetails.refetch()} />
      ) : fixSkuDetails.isLoading ? (
        <Loader />
      ) : (
        <FormWrap
          key={id}
          id={id}
          form={form}
          title="update_fix_sku"
          request={updateFixSku}
          cancelText="Cancel"
          okText="Save"
          data={fixSkuDetails.data}
          onSuccess={() => {
            const url = createUrlWithFiltersFromCurrentURL('/fixed-sku')
            history.push(url)
          }}
          onCancel={() => {
            const url = createUrlWithFiltersFromCurrentURL('/fixed-sku')
            history.push(url)
          }}
          cleanBeforeSending={(oldData) => {
            oldData.store = storeId

            if (oldData.details && Array.isArray(oldData.details)) {
              oldData.details = oldData.details.map((detail) => {
                const cleanedDetail = {}
                if (detail._id) {
                  cleanedDetail._id = detail._id
                }
                Object.keys(detail).forEach((productType) => {
                  if (productType === '_id') return
                  if (
                    detail[productType] &&
                    typeof detail[productType] === 'object'
                  ) {
                    const cleanedProductData = {}
                    Object.keys(detail[productType]).forEach((key) => {
                      const value = detail[productType][key]
                      if (
                        value !== null &&
                        value !== undefined &&
                        value !== ''
                      ) {
                        cleanedProductData[key] = value
                      }
                    })
                    if (Object.keys(cleanedProductData).length > 0) {
                      cleanedDetail[productType] = cleanedProductData
                    }
                  }
                })
                return cleanedDetail
              })
            }

            if (oldData.mainPicture) {
              let main = oldData.mainPicture.fileList
                ? oldData.mainPicture?.fileList
                : oldData.mainPicture
              if (!Array.isArray(main)) {
                main = [main]
              }
              const newPic = main.map((file) =>
                file.response ? file.response : file
              )
              oldData.mainPicture = newPic.length ? newPic[0] : {}
            } else {
              oldData.mainPicture = {}
            }

            if (oldData.otherPictures) {
              const otherPic = oldData.otherPictures.fileList
                ? oldData.otherPictures?.fileList
                : oldData.otherPictures
              oldData.otherPictures = (Array.isArray(otherPic) ? otherPic : []).map(
                (file) => (file.response ? file.response : file)
              )
            } else {
              oldData.otherPictures = []
            }

            return oldData
          }}
          invalidate={['getFixSku', 'getFixSkuById']}
        >
          <div className="section-from-vertical">
            <FormBlock form={form} formTemplate={FixSkuDescriptionForm} />
            <FormBlock form={form} formTemplate={FixSkuSizesForm} />
            {!user.franchiseStore && (
              <FormBlock form={form} formTemplate={FixSkuMediaForm} />
            )}
          </div>
        </FormWrap>
      )}
    </>
  )
}

export default EditFixSku
